import { useLayoutEffect, useRef } from 'react';
import './Tabs.css';

const KEY_DIRECTION = {
  ArrowLeft: -1,
  ArrowRight: 1,
};

const positionHighlight = (list, tab, kind) => {
  list.style.setProperty(`--tabs-${kind}-left`, `${tab.offsetLeft}px`);
  list.style.setProperty(`--tabs-${kind}-width`, `${tab.offsetWidth}px`);
  if (kind === 'hover') {
    list.style.setProperty('--tabs-hover-top', `${tab.offsetTop}px`);
    list.style.setProperty('--tabs-hover-height', `${tab.offsetHeight}px`);
  }
};

const Tabs = ({
  items,
  value,
  onValueChange,
  ariaLabel,
  variant = 'line',
  className = '',
}) => {
  const tabRefs = useRef(new Map());
  const listRef = useRef(null);
  const hoveredValue = useRef(null);
  const canCollapseToIcons = items.every((item) => item.icon);
  const tabValuesKey = items.map((item) => item.value).join('\u0000');

  // Callers often build items inline, so effects must not key on the array
  // identity: that would clone and measure the list on every parent render.
  // Rendered label, badge and count changes are picked up by MutationObserver.
  useLayoutEffect(() => {
    if (!['line', 'segment'].includes(variant) || !canCollapseToIcons) return;
    const list = listRef.current;
    const measure = () => {
      // Measure an unconstrained copy so collapsed labels cannot feed back into
      // the next measurement. This also includes localized text and badges.
      const copy = list.cloneNode(true);
      copy.removeAttribute('data-compact');
      copy.removeAttribute('id');
      copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      copy.setAttribute('aria-hidden', 'true');
      copy.inert = true;
      Object.assign(copy.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        width: 'max-content',
        maxWidth: 'none',
      });
      list.parentElement.appendChild(copy);
      const contentWidth = copy.getBoundingClientRect().width;
      copy.remove();
      list.style.setProperty('--tabs-content-width', `${contentWidth}px`);
      list.toggleAttribute('data-compact', list.getBoundingClientRect().width + 1 < contentWidth);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(list);
    const contentObserver = typeof MutationObserver === 'undefined' ? null : new MutationObserver(measure);
    contentObserver?.observe(list, { subtree: true, childList: true, characterData: true });
    window.addEventListener('resize', measure);
    document.fonts?.addEventListener('loadingdone', measure);
    return () => {
      observer?.disconnect();
      contentObserver?.disconnect();
      window.removeEventListener('resize', measure);
      document.fonts?.removeEventListener('loadingdone', measure);
    };
  }, [canCollapseToIcons, variant]);

  useLayoutEffect(() => {
    if (variant !== 'line') return;
    const list = listRef.current;
    const update = () => {
      for (const [kind, tabValue] of [['active', value], ['hover', hoveredValue.current]]) {
        const tab = tabRefs.current.get(tabValue);
        if (!tab) continue;
        positionHighlight(list, tab, kind);
      }
    };
    update();
    // Labels and counts can resize independently of the container.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(list);
    tabRefs.current.forEach((tab) => observer?.observe(tab));
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [value, tabValuesKey, variant]);

  if (!ariaLabel) {
    throw new Error('Tabs requires an ariaLabel.');
  }
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error('Tabs requires at least one item.');
  }
  if (typeof onValueChange !== 'function') {
    throw new Error('Tabs requires an onValueChange callback.');
  }
  if (!['line', 'segment', 'folder'].includes(variant)) {
    throw new Error(`Tabs variant "${variant}" is not supported.`);
  }

  const values = items.map((item) => item.value);
  if (new Set(values).size !== values.length) {
    throw new Error('Tabs item values must be unique.');
  }
  if (!values.includes(value)) {
    throw new Error(`Tabs value "${value}" does not match an item.`);
  }

  const enabledItems = items.filter((item) => !item.disabled);
  const activeItem = items.find((item) => item.value === value);
  if (enabledItems.length === 0) {
    throw new Error('Tabs requires at least one enabled item.');
  }
  if (activeItem.disabled) {
    throw new Error('Tabs active item cannot be disabled.');
  }

  const moveFocus = (event, item) => {
    const direction = KEY_DIRECTION[event.key];
    const isBoundaryKey = event.key === 'Home' || event.key === 'End';
    if (!direction && !isBoundaryKey) return;

    event.preventDefault();
    const currentIndex = enabledItems.findIndex(({ value: itemValue }) => itemValue === item.value);
    let targetIndex;

    if (event.key === 'Home') targetIndex = 0;
    else if (event.key === 'End') targetIndex = enabledItems.length - 1;
    else targetIndex = (currentIndex + direction + enabledItems.length) % enabledItems.length;

    const target = enabledItems[targetIndex];
    onValueChange(target.value);
    tabRefs.current.get(target.value)?.focus();
  };

  return (
    <div
      ref={listRef}
      className={`rds-tabs rds-tabs--${variant}${canCollapseToIcons ? ' rds-tabs--responsive-icons' : ''} ${className}`.trim()}
      role="tablist"
      aria-label={ariaLabel}
      onPointerLeave={() => {
        hoveredValue.current = null;
        listRef.current.removeAttribute('data-hovering');
      }}
    >
      {variant === 'line' && <>
        <span className="rds-tabs__hover" aria-hidden="true" />
        <span className="rds-tabs__indicator" aria-hidden="true" />
      </>}
      {items.map((item) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            ref={(node) => {
              if (node) tabRefs.current.set(item.value, node);
              else tabRefs.current.delete(item.value);
            }}
            type="button"
            role="tab"
            id={item.id}
            aria-controls={item.controls}
            aria-label={item.ariaLabel}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            className="rds-tabs__tab"
            onClick={() => onValueChange(item.value)}
            onKeyDown={(event) => moveFocus(event, item)}
            onPointerEnter={(event) => {
              if (variant !== 'line') return;
              if (item.disabled || event.pointerType === 'touch') {
                hoveredValue.current = null;
                listRef.current.removeAttribute('data-hovering');
                return;
              }
              hoveredValue.current = item.value;
              const tab = event.currentTarget;
              const list = listRef.current;
              positionHighlight(list, tab, 'hover');
              list.setAttribute('data-hovering', 'true');
            }}
          >
            {item.icon && <span className="rds-tabs__icon" aria-hidden="true">{item.icon}</span>}
            <span className="rds-tabs__label">{item.label}</span>
            {item.count !== undefined && <span className="rds-tabs__count">{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
